import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { tableHelper } from '@builttocreate/engine-utils';

import ImageManager from '../../../misc/ImageManager';
import FieldTypes from '../../../../constants/FieldTypes';

const { getRowOrder } = tableHelper;

const StyledWrapper = styled.div`
  width: 100%;
  overflow: hidden;

  /*
   * Only use height when you don't need height 
   * measurements of the table field.
   */
  ${(props) => {
    return props.measure ? '' : 'height: 100%;';
  }}

  background-color: ${(props) => props.backgroundColor};
  border-radius: ${(props) => props.borderRadius};
  border: ${(props) => props.border};
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}

  & > div > div > div > div {
    max-width: 100%;
  }
`;

const TableImageField = ({
  fieldId,
  fieldIdentifier,
  fieldPositionId,

  columnType,
  column,
  rowId,
  rowIndex,
  rowOrder,
  columnIdentifier,

  tableColumns,

  onCellUpload,
  onCellChange,
  onFocus,

  onFileClick,
  onFileDelete,

  value,
  cellValue,
  disabled,
  enableSystemFilePicker,

  fontColor,
  primaryColor,
  dangerColor,
  backgroundColor,
  borderRadius,
  borderWidth,
  borderColor,
  margin,
  padding,

  //Used for calculating width and height of element
  measure,
  onMeasureCompleted

}) => {

  const [wrapperRef, setWrapperRef] = useState(null);
  const [loadedImages, setLoadedImages] = useState(false);

  const columnObject = tableColumns.find((col) => col._id === column);

  useEffect(() => {
    if (onMeasureCompleted && wrapperRef && loadedImages) {
      onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, wrapperRef.getBoundingClientRect());
    }
  }, [wrapperRef, loadedImages]);

  const handleUpload = (loadingCallback, errorCallback, files) => {
    if (onCellUpload && rowId) onCellUpload(fieldId, fieldIdentifier, fieldPositionId, rowId, column, files, loadingCallback, errorCallback);
  };

  const handleDelete = (i, urlObject) => {

    const nextImageValues = [...cellValue];
    nextImageValues.splice(i, 1);

    onCellChange(fieldId, fieldIdentifier, fieldPositionId, rowId, column, nextImageValues);

    /**
     * Important Note: onFileDelete is simply to notify about the deletion event. The actual
     * removal of the image is handled by onCellChange.
     */
    if (onFileDelete) onFileDelete(fieldId, fieldIdentifier, fieldPositionId, urlObject, i, { rowId, columnId: column });

  };

  const handleClick = (i, urlObject) => {
    if (onFileClick) onFileClick(fieldId, fieldIdentifier, fieldPositionId, urlObject, i, { rowId, columnId: column });
  };

  const handleFocus = () => {

    if (onFocus && Object.keys(columnObject).length > 0) {
      onFocus(fieldId, fieldIdentifier, fieldPositionId, {}, rowId, column, columnIdentifier, {}, columnType);
    }

  };

  const handleLoadCompleted = () => {
    setLoadedImages(true);
  };


  /**
   * We join width and color to a single border style because chrome displays
   * the border as a two tone color if you use border-color and border-width
   * indvidually.
   */
  const border = `${borderWidth}px solid ${borderColor}`;

  const isRowDeleted = !getRowOrder(rowOrder, value)[rowIndex];

  /**
   * If the column width is less than maxImageWidth then the column width
   * will be applied as the maxImageWidth so that images would be placed within
   * cell boundary
   */
  const effectiveMaxImageWidth = columnObject?.maxImageWidth || 200;
  const effectiveMaxImageHeight = columnObject?.maxImageHeight || 200;
  const columnWidth = columnObject?.width;
  const validMaxImageWidth = columnWidth && columnWidth < effectiveMaxImageWidth ? columnWidth : effectiveMaxImageWidth;

  return (
    <StyledWrapper
      data-testid={`${fieldPositionId}-table-image-field`}
      {...{
        backgroundColor,
        border,
        borderRadius,
        margin,
        measure
      }}
      ref={(e) => {
        if (e && !wrapperRef) setWrapperRef(e);
      }}
    >
      <ImageManager
        {...{
          type: FieldTypes.image, // type is need to only allow images for image field
          value: cellValue,
          multiple: true,
          disabled: disabled || isRowDeleted,
          enableSystemFilePicker,
          enforceMaxDimensions: true,

          primaryColor,
          dangerColor,
          fontColor,
          borderColor,
          borderWidth,
          borderRadius,
          backgroundColor,
          padding, //Default spacing around images
          maxWidth: `${validMaxImageWidth}px`,
          maxHeight: `${effectiveMaxImageHeight}px`,

          onDelete: handleDelete,
          onFocus: handleFocus,
          onUpload: handleUpload,
          onClick: handleClick,
          onLoadCompleted: handleLoadCompleted,
        }}
      />
    </StyledWrapper>
  );

};

export default React.memo(TableImageField);
